import { ConfigurationError, InitializationError } from '@cian/core/errors/shared/index.js';
import { None, Some } from '@cian/core/option/index.js';
import { Result } from '@cian/core/result/index.js';
import { iteratorMap } from '@cian/core/iterator/index.js';
import { initAppEnv, getAppEnv } from '@cian/core/env/browser/index.js';
import { createSystemSource } from './sources/system.js';
import { Config } from '../shared/config/config.js';
import { ConfigSafe } from '../shared/config/safe.js';
import { validateSetting } from '../shared/setting/setting.js';
import { SYSTEM_KEYS } from '../shared/constants.js';
import { defaultFilters, createMatcher, applicationFilter, mcsRunEnvFilter, mcsRunEnvTypeFilter } from '../shared/internal/filter/filter.js';
import '../shared/internal/filter/comparator.js';
import { SendableStorage } from '../shared/internal/source/storage.js';
import { DeprecatedError, DeprecatedStatus } from '@cian/core/conventions/index.js';

const NAMESPACE = 'config.browser';
// oxlint-disable-next-line typescript/no-explicit-any
let config = null;
// oxlint-disable-next-line typescript/no-explicit-any
let configUnsafe = null;
/**
 * @deprecated конфиг самоинициализируется, вызов этого метода можно удалить
 * @throws выбрасывает исключение
 *
 * @export
 * @param {object} [params]
 */
// oxlint-disable-next-line typescript/no-unused-vars
function setup(params) {
    if (process.env.NODE_ENV === 'development' ||
        process.env.NODE_ENV === 'test' ||
        process.env.IS_CYPRESS_TESTING === '1') {
        throw new DeprecatedError({
            message: 'Метод @cian/config/browser setup() более не доступен для использования. @cian/config инициализируется автоматически. Удалите вызов метода',
            domain: `config.browser.setup`,
            depNumber: 25,
            status: DeprecatedStatus.NotAvailable,
        });
    }
}
function initConfigUnsafe() {
    if (!configUnsafe) {
        configUnsafe = new Config(initConfig());
    }
    return configUnsafe;
}
/**
 * Алиас над {@link initConfigUnsafe}
 *
 * @deprecated Пользуйтесь именованными методами
 * @alias initConfigUnsafe
 */
const init = initConfigUnsafe;
function initConfig() {
    if (!config) {
        initAppEnv().unwrap();
        config = loadConfig();
    }
    return config;
}
function getConfigUnsafe() {
    if (!configUnsafe) {
        throw new ConfigurationError({
            message: 'Config must be created before accessing it. Forgot to call init() before accessing config?',
            domain: 'config.getConfigUnsafe',
        });
    }
    return configUnsafe;
}
const getConfig = getConfigUnsafe;
function getConfigSafe() {
    if (!config) {
        throw new ConfigurationError({
            message: 'Config must be created before accessing it. Forgot to call init() before accessing config?',
            domain: 'config.getConfigSafe',
        });
    }
    return config;
}
function initByDump(params) {
    let dump;
    if (typeof params === 'string') {
        // TODO: Необходимо учитывать возможные мажорные изменения в модели дампа
        if (window._cianConfig && window._cianConfig[params]) {
            dump = window._cianConfig[params];
        }
        else {
            return None();
        }
    }
    else {
        dump = params;
    }
    const config = new ConfigSafe({ source: new SendableStorage(dump) });
    return Some(config);
}
function initDump(projectName) {
    if (!window._cianConfig) {
        window._cianConfig = {};
    }
    const store = window._cianConfig;
    if (!store[projectName]) {
        store[projectName] = [];
    }
    const dump = store[projectName];
    if (!dump.watcher) {
        dump.watcher = document.createElement('span');
        dump.push = dumpPush;
        dump.concat = dumpConcat;
    }
    function dumpPush(...args) {
        const result = Array.prototype.push.apply(this, args);
        this.watcher?.dispatchEvent(new CustomEvent('update', { detail: this }));
        return result;
    }
    function dumpConcat(...args) {
        const result = Array.prototype.concat.apply(this, args);
        this.watcher?.dispatchEvent(new CustomEvent('update', { detail: result }));
        result.watcher = this.watcher;
        result.concat = dumpConcat;
        result.push = dumpPush;
        return result;
    }
    return dump;
}
function loadConfig() {
    const appEnv = getAppEnv().unwrap();
    const systemSource = createSystemSource(appEnv);
    const systemConfig = new ConfigSafe({ source: systemSource });
    const projectName = systemConfig.get('projectName').unwrap();
    const dump = initDump(projectName);
    Result.collect(iteratorMap(dump, s => validateSetting(s)))
        .mapErr(err => new InitializationError({
        message: `В дампе настроек _cianConfig['${projectName}'], есть невалидная настройка`,
        domain: 'config.loadConfig',
        details: err,
    }))
        .unwrap();
    // TODO: Сейчас настройки передаются по ссылке, что небезопасно в случае микрофронтов
    const dumpSource = new SendableStorage(dump);
    const matcher = defaultContextMatcher(appEnv);
    // TODO: Для SPA-приложений нужно подумать об очистке подписок
    dump.watcher?.addEventListener('update', (e) => {
        dumpSource.batchAdd(e.detail);
    });
    const config = new ConfigSafe({ source: [systemSource, dumpSource], matcher });
    return systemConfig
        .get(SYSTEM_KEYS.requestId)
        .map(requestId => config.attach({
        requestId,
        // TODO: Выяснить, нужно ли устанавливать
        instanceId: requestId,
    }))
        .unwrapOr(config);
}
function defaultContextMatcher(appEnv) {
    const filters = defaultFilters();
    return createMatcher([
        ...filters,
        applicationFilter(appEnv.application),
        mcsRunEnvFilter(appEnv.mcsRunEnv),
        mcsRunEnvTypeFilter(appEnv.mcsRunEnvType),
    ], NAMESPACE + '.matcher');
}
/**
 * @private
 */
function __clean() {
    configUnsafe = null;
    config = null;
}

export { __clean, getConfig, getConfigSafe, getConfigUnsafe, init, initByDump, initConfig, initConfigUnsafe, setup };
//# sourceMappingURL=interface.js.map
